import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { capitalize, sortBy } from 'lodash';

import { AdminPageHeader } from '../../Admin/Common/AdminPagesHeader';
import { Table } from '../../shared';
import {
    NAVIGATION_DATA_TYPES,
    QUERY_KEYS,
    SCORECARD_REPORT,
    SCORE_CARD_CUSTOMER_COLUMNS,
    SYMBOL,
    LOCAL_STORAGE,
} from '../../../constants';
import { usePagination } from '../../../hooks';
import { setNavigationState } from '../../../redux';
import { popup, getPlatformById, getAllScoreCardDetails, getLocalStorageItem, setLocalStorageItem } from '../../../utils';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export function ScoreCardReport() {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [search, setSearch] = useState('');
    const [response, setResponse] = useState({ data: [], count: 0 });
    const [selectedPt, setSelectedPt] = useState(undefined);
    const pagination = usePagination(response.data.length, response.count);
    const accountId = useSelector((state) => state.accountArray.selectedAccount);
    const { navigationState } = useSelector((state) => state.navigationState);

    const prepareAssistantContext = () => {
        try {
            const accountId = getLocalStorageItem(LOCAL_STORAGE.ACCOUNT_ID);
            if (!accountId) return;
            const total = response.count || 0;
            const payload = {
                type: 'scorecards_report',
                accountId,
                totalScorecards: total,
            };
            setLocalStorageItem('ssc_ai_selection', payload);
        } catch (e) {
            // best-effort; do not impact scorecard UX
        }
    };

    const platformsQuery = useQuery({
        queryKey: [QUERY_KEYS.PLATFORMS, accountId],
        queryFn: async () => await getPlatformById({ accountId: accountId }),
        select: (data) => {
            const newPlatforms = [{ id: '', name: 'All Platforms' }, ...sortBy(data.data, 'name')];
            return newPlatforms;
        },
        placeholderData: [{ id: '', name: 'All Platforms' }],
    });

    const filterConfig = [
        {
            id: 'platformFilter',
            name: 'Platform',
            selectedId: selectedPt,
            handleFunction: (_v, id) => {
                setSelectedPt(id);
            },
            options: platformsQuery.data,
        },
    ];

    const [debounceSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error } = useQuery({
        queryKey: [
            QUERY_KEYS.SCORECARD_DETAILS,
            accountId,
            selectedPt,
            debounceSearch,
            pagination.offset,
            pagination.limit,
            navigationState?.type,
            navigationState?.id,
        ],
        queryFn: async () =>
            await getAllScoreCardDetails({
                account_id: accountId,
                selectedPlatform: { id: selectedPt },
                offset: pagination.offset,
                limit: pagination.limit,
                search: debounceSearch,
                orderByGrade: true,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
    });

    useEffect(() => {
        if (data.data && isFetched) {
            setResponse({ data: data.data, count: data.count });
        }
    }, [data, isFetched]);

    if (isError) popup('error', error.message);

    useEffect(() => {
        if (
            navigationState &&
            navigationState?.type === NAVIGATION_DATA_TYPES.PLATFORM &&
            navigationState?.id
        ) {
            setSelectedPt(navigationState.id);
        }
        return () => {
            dispatch(setNavigationState(null));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigationState]);

    return (
        <>
            <AdminPageHeader
                heading={SCORECARD_REPORT.PAGE_NAME}
                searchBarPlaceholder={SCORECARD_REPORT.SEARCH_BAR_PLACEHOLDER}
                setSearch={setSearch}
                count={pagination.dataCount}
                setCurrentPage={pagination.setCurrentPage}
                backbutton
                filterConfig={filterConfig}
                formatOptionLabel={({ label, value }) => {
                    if (!value.id) return label;
                    return (
                        <span className=' d-flex align-items-center text-txt'>
                            <span
                                title={capitalize(value.grade)}
                                className={`dot-circle ${value.grade} me-2`}
                            ></span>
                            {label}
                            <sup>{SYMBOL.REGISTERED}</sup>
                        </span>
                    );
                }}
            />
            <div className='d-flex justify-content-end mb-2 px-1 px-lg-0'>
                <button
                    type='button'
                    className='ssc-card-btn2 text-nowrap'
                    onClick={prepareAssistantContext}
                    title='Prime the AI assistant with the current scorecards view. Then open the Assistant to review and plan improvements.'
                >
                    Ask Assistant
                </button>
            </div>
            <Table
                columns={SCORE_CARD_CUSTOMER_COLUMNS}
                data={response.data}
                pagination={pagination}
                pageName={SCORECARD_REPORT.PAGE_NAME}
                stickyColumns={['Hiring Client']}
                initialSort={searchParams.has('sort_by') ? searchParams.get('sort_by') : null}
                columnToSort={
                    searchParams.has('column_sort_index')
                        ? parseInt(searchParams.get('column_sort_index'))
                        : 0
                }
                isLoading={isFetching}
            />
        </>
    );
}
